import '@fontsource/roboto/latin-400.css';
import '@fontsource/roboto/latin-600.css';
import '@fontsource/roboto/latin-700.css';
import '@fontsource/material-icons/400.css';
import 'ol/ol.css';
import './stylesheets/vegkart.less';

import { createRoot } from 'react-dom/client';

import { Provider } from 'react-redux';

import { initFlagsFromHash } from '@/featureFlags';
import { initStore } from '@/state/store';
import { StrictMode } from 'react';
import { Bootstrapper } from './bootstrap/Bootstrapper';
import { parseConfig } from './bootstrap/config';
import ErrorBoundary from './components/error/ErrorBoundary';
import { Vegkart } from './components/Vegkart';
import { config, getDocumentTitle } from './config';
import { Integration } from './middleware/integration';
import { API_VERSION_STORAGE_KEY } from './state';
import { ConfigAndDataProvider } from './state/context/ConfigAndDataContext';
import { version } from './version';
import { getApiVersion, sanitizeUrl } from '@/utils/Utils';
import { shouldShowStartupChangelog } from '@/utils/changelog';
import { handleOAuthPopup } from '@/utils/pkce';
import { runtimeEnvironment } from '@/config';
import { DatakatalogStandalone } from './components/avgrensning/datakatalog/DatakatalogStandalone';

// Handle standalone Datakatalog and OAuth callbacks before normal app initialization.
if (
    new URLSearchParams(window.location.search).has('datakatalogTab') ||
    new URLSearchParams(window.location.search).has('datakatalogSrc')
) {
    document.title = `Datakatalogen – ${getDocumentTitle()}`;
    createRoot(document.getElementById('root')!).render(
        <StrictMode>
            <DatakatalogStandalone />
        </StrictMode>
    );
} else if (!handleOAuthPopup()) {
    // Not an OAuth popup, proceed with normal app initialization

    sanitizeUrl();

    document.title = getDocumentTitle();

    initFlagsFromHash();
    const integration: Integration = new Integration();
    integration.setConfig(parseConfig(version, config));
    const store = initStore(integration);
    const previousVersion = localStorage.getItem('version');
    localStorage.setItem('lastVersion', previousVersion ?? '');
    if (previousVersion !== version.version) {
        localStorage.setItem('version', version.version);
        if (
            shouldShowStartupChangelog(
                process.env.changelog ?? '',
                previousVersion,
                version.version,
                runtimeEnvironment()
            )
        ) {
            window.location.href =
                window.location.href.slice(-1) != '/'
                    ? window.location.href + '/splash:changelog'
                    : window.location.origin + '/#/splash:changelog';
        }
    }
    sessionStorage.setItem(API_VERSION_STORAGE_KEY, getApiVersion());

    const container = document.getElementById('root');
    const loading = (
        <div style={{ paddingTop: '25%', textAlign: 'center', fontFamily: 'sans-serif' }}>
            Vent litt mens Vegkart laster…
        </div>
    );
    const root = createRoot(container!);
    root.render(
        <StrictMode>
            <Provider store={store} key="provider">
                <ErrorBoundary>
                    <Bootstrapper
                        fallback={loading}
                        integration={integration}
                        store={store.getState()}
                    >
                        <ConfigAndDataProvider integration={integration}>
                            <Vegkart />
                        </ConfigAndDataProvider>
                    </Bootstrapper>
                </ErrorBoundary>
            </Provider>
        </StrictMode>
    );
}
